'use client';

import type { ReactNode } from 'react';
import { tv } from '../_lib/utils';
import { Author, type AuthorProps } from '../Author';
import type { leftCenterRightValues, mediumLargeValues } from '../_lib/designTypesStore';
import { Container } from '../Container';

const styles = tv({
	slots: {
		base: '',
		quote: 'open-quote close-quote',
		authors: 'flex flex-row gap-3',
	},
	variants: {
		alignment: {
			center: {
				base: 'text-center',
				authors: 'justify-center',
			},
			left: {
				authors: 'justify-start',
			},
			right: {
				base: 'text-right',
				authors: 'justify-end',
			},
		},
		size: {
			large: {
				quote: 'text-quote-md',
			},
			medium: {
				quote: 'text-quote-sm',
			},
		},
	},
});

const contentStyles = tv({
	slots: {
		base: 'grid gap-responsive-lg',
	},
	variants: {
		alignment: {
			center: {
				base: 'py-responsive-lg',
			},
			left: {
				base: 'py-responsive-xs pl-spacing-lg border-l-2 border-foreground-brand-primary',
			},
			right: {
				base: 'py-responsive-xs pr-spacing-lg border-r-2 border-foreground-brand-primary',
			},
		},
	},
});

export type QuoteProps = {
	alignment?: (typeof leftCenterRightValues)[number];
	copy: ReactNode;
	className?: string;
	size?: (typeof mediumLargeValues)[number];
	authors?: AuthorProps[];
};

export function Quote(props: QuoteProps) {
	const alignment = props.alignment ?? 'center';
	const size = props.size ?? 'large';
	const { base, quote, authors } = styles({ alignment, size });
	const { base: content } = contentStyles({ alignment });

	return (
		<figure className={base({ className: props.className })}>
			<Container size='editorial-wide'>
				<div className={content()}>
					<blockquote className={quote()}>{props.copy}</blockquote>
					{props.authors && (
						<div className={authors()}>
							{props.authors.map((author, index) => {
								return author.name ? (
									<figcaption key={`author-${index}-${author.name}`}>
										<Author alignment={alignment} avatarMobile image={author.image} name={author.name} meta={author.meta} />
									</figcaption>
								) : null;
							})}
						</div>
					)}
				</div>
			</Container>
		</figure>
	);
}
